import { useState } from 'react'
import api from '../../../utils/api'

/**
 * Component for searching and adding songs to a playlist
 */
function SongSearch({ playlistId, existingSongIds, onSongAdded, loading, setLoading, setError }) {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [showSearchResults, setShowSearchResults] = useState(false)

  const handleSearch = async () => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([])
      setShowSearchResults(false)
      return
    }

    try {
      setLoading(true)

      const response = await api.get('admin/playlists/search-songs', {
        params: { q: searchQuery },
      })

      // Filter out songs that are already in the playlist
      const filteredResults = response.data.songs.filter(
        (song) => !existingSongIds.includes(song.id)
      )

      setSearchResults(filteredResults)
      setShowSearchResults(true)
    } catch (err) {
      console.error('Error searching songs:', err)
      setError(`Failed to search songs: ${err.response?.data?.error || 'Unknown error'}`)
    } finally {
      setLoading(false)
    }
  }

  const addSong = async (song) => {
    try {
      setLoading(true)

      await api.post(`admin/playlists/${playlistId}/add-song`, { songId: song.id })

      // Clear search results and query
      setSearchResults([])
      setSearchQuery('')
      setShowSearchResults(false)

      // Notify parent
      onSongAdded(song)
    } catch (err) {
      console.error('Error adding song:', err)
      setError(`Failed to add song: ${err.response?.data?.error || 'Unknown error'}`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='mb-6 rounded border p-4'>
      <h3 className='mb-3 text-lg font-semibold'>Add Songs</h3>

      <div className='mb-4 flex space-x-2'>
        <input
          type='text'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder='Search for songs...'
          className='flex-grow rounded border p-2'
        />
        <button
          onClick={handleSearch}
          className='bg-blue-500 hover:bg-blue-600 text-white rounded px-4 py-2'
          disabled={loading}
        >
          Search
        </button>
      </div>

      {showSearchResults && (
        <div className='mt-2 overflow-hidden rounded border'>
          <div className='bg-gray-100 px-4 py-2 font-semibold'>Search Results</div>

          {searchResults.length === 0 ? (
            <div className='text-gray-500 p-4 text-center'>
              No songs found. Try a different search term.
            </div>
          ) : (
            <div className='divide-y'>
              {searchResults.map((song) => (
                <div
                  key={song.id}
                  className='hover:bg-gray-50 flex items-center justify-between p-3'
                >
                  <div>
                    <div className='font-medium'>{song.title}</div>
                    <div className='text-gray-600 text-sm'>{song.artists.join(', ')}</div>
                  </div>
                  <button
                    onClick={() => addSong(song)}
                    className='bg-green-500 hover:bg-green-600 text-white rounded px-3 py-1'
                    disabled={loading}
                  >
                    Add
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default SongSearch
